import { toggleArray } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import useCampaignContacts from '@hooks/useCampaignContacts'
import useSelector from '@hooks/useSelector'

import type {
  CampaignModel, ContactModel, ImageModel, UserModel,
} from '@models/types'

export type Recipient = {
  email: string,
  full_name: string,
  id: number,
  image_url?: string,
  image_id?: number,
  type: string,
}

const defaultState = {
  message: '',
  selectedRecipients: [] as Recipient[],
  subject: '',
}

type SetState = (state: Partial<typeof defaultState>) => void

type ContactOrUserWithImage =
  (ContactModel | UserModel) & { image?: ImageModel }

const mapToRecipients = (contactsOrUsers: ContactOrUserWithImage[]) => (
  contactsOrUsers.map((recipient) => {
    const {
      email, full_name = '', id, image, type,
    } = recipient

    return {
      email, full_name, id, image_url: image?.thumb_url, type,
    }
  })
)

type ToggleSelectedUserParams = {
  recipient: ContactOrUserWithImage,
  selectedRecipients: Recipient[],
  setState: SetState,
}

const toggleSelectedRecipient = (params: ToggleSelectedUserParams) => {
  const { recipient, selectedRecipients, setState } = params

  const {
    email, full_name, id, image, type,
  } = recipient

  const updatedRecipient: Recipient = {
    email,
    full_name: full_name || '',
    id,
    image_url: image?.thumb_url,
    type,
  }

  const updatedRecipients = toggleArray(selectedRecipients, updatedRecipient, { deepCompare: true })

  setState({ selectedRecipients: updatedRecipients })
}

type EmailRecipientParams = {
  campaign: CampaignModel,
}
const useEmailRecipient = (params: EmailRecipientParams) => {
  const { campaign } = params

  const [state, setState] = useSetState(defaultState)
  const {
    message, selectedRecipients, subject,
  } = state

  const contacts = useSelector(reduxState => reduxState.entities.contacts)

  const campaignContactsPayload = useCampaignContacts({
    filters: {
      campaign_id: campaign.id,
    },
    performHttpRequests: !!campaign.id,
    requestOptions: {
      include_campaign_contact_contact: true,
    },
  })

  const { filteredCampaignContacts } = campaignContactsPayload

  const filteredContacts = filteredCampaignContacts
    .map(campaignContact => contacts[campaignContact.contact_id])
    .filter(c => c)

  const hasSelectedUsers = !!selectedRecipients.length

  const selectedContactIds = selectedRecipients.reduce((ids: number[], recipient) => {
    if (recipient.type === 'Contact') ids.push(recipient.id)
    return ids
  }, [])

  const unselectedContacts = filteredContacts.filter(contact => !selectedContactIds.includes(contact.id))

  return {
    callbacks: {
      mapToRecipients: (contactsOrUsers: (ContactModel | UserModel)[]) => mapToRecipients(contactsOrUsers),
      setModalState: (values: Partial<typeof defaultState>) => setState(values),
      toggleRecipient: (recipient: ContactModel | UserModel) => (
        toggleSelectedRecipient({ recipient, selectedRecipients, setState })
      ),
    },
    unselectedContacts,
    hasSelectedUsers,
    message,
    selectedRecipients,
    subject,
  }
}

export type UseSelectEmailRecipientModalPayload = ReturnType<typeof useEmailRecipient>

export default useEmailRecipient
